import Phaser from 'phaser';
import { authManager } from '../auth/AuthManager.js';

export class TitleScene extends Phaser.Scene {
  constructor() {
    super({ key: 'TitleScene' });
  }
  
  create() {
    const { width, height } = this.scale;
    
    // Background
    this.add.rectangle(width/2, height/2, width, height, 0x1a1a2e);
    
    // Coffee cup animation (simple version)
    this.coffeeCup = this.add.text(width/2, 180, '☕', {
      fontSize: '96px'
    }).setOrigin(0.5);
    
    // Steam animation
    this.tweens.add({
      targets: this.coffeeCup,
      y: 170,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    
    // Title
    this.add.text(width/2, 300, 'PROJECT COFFEE', {
      fontSize: '48px',
      fill: '#f4d03f',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    
    // Subtitle
    this.add.text(width/2, 350, 'A game about coffee, identity, and what makes us human', {
      fontSize: '16px',
      fill: '#888'
    }).setOrigin(0.5);
    
    // Loading text (while checking auth)
    this.statusText = this.add.text(width/2, 450, 'Loading...', {
      fontSize: '18px',
      fill: '#aaa'
    }).setOrigin(0.5);
    
    // Check auth status
    this.checkAuth();
  }
  
  async checkAuth() {
    try {
      const user = await authManager.init();
      
      // Check for saved player name
      const savedName = localStorage.getItem('projectCoffee_playerName');
      if (savedName) {
        window.playerName = savedName;
        if (window.dialogueManager) {
          window.dialogueManager.playerName = savedName;
        }
        // Show continue if we have a saved name (regardless of auth)
        this.showContinueOption();
      } else {
        this.showStartOptions();
      }
    } catch (error) {
      console.error('Auth error:', error);
      // Still check for saved name even if auth fails
      const savedName = localStorage.getItem('projectCoffee_playerName');
      if (savedName) {
        window.playerName = savedName;
        if (window.dialogueManager) {
          window.dialogueManager.playerName = savedName;
        }
        this.showContinueOption();
      } else {
        this.showStartOptions();
      }
    }
  }
  
  showContinueOption() {
    const { width } = this.scale;
    this.statusText.destroy();
    
    const savedName = window.playerName || 'friend';
    
    // Welcome back message
    this.add.text(width/2, 420, `Welcome back, ${savedName}!`, {
      fontSize: '20px',
      fill: '#4CAF50'
    }).setOrigin(0.5);
    
    // Continue button
    this.createButton(width/2, 480, 'CONTINUE', async () => {
      try {
        await authManager.signInAnonymously();
      } catch (error) {
        console.error('Auth error (continuing anyway):', error);
      }
      this.startGame();
    });
    
    // New game button
    this.createButton(width/2, 530, 'NEW GAME', async () => {
      // Reset game state and clear saved name
      localStorage.removeItem('projectCoffee_playerName');
      window.playerName = null;
      await authManager.saveGameState(authManager.getDefaultGameState());
      // Show start options again
      this.scene.restart();
    }, true);
  }
  
  showStartOptions() {
    const { width } = this.scale;
    this.statusText.destroy();
    
    // Name input label
    this.add.text(width/2, 400, 'What\'s your name, barista?', {
      fontSize: '18px',
      fill: '#aaa'
    }).setOrigin(0.5);
    
    // Create HTML input for name (Phaser text input is awkward)
    this.createNameInput(width/2, 440);
    
    // Quick play button (HTML button for better accessibility)
    this.createStartButton(width/2, 510);
    
    // Hint
    this.add.text(width/2, 570, 'Progress saves automatically', {
      fontSize: '12px',
      fill: '#555'
    }).setOrigin(0.5);
  }
  
  createNameInput(x, y) {
    // Create a DOM input element positioned over the canvas
    let input = document.getElementById('player-name-input');
    const container = document.getElementById('game-container');
    
    if (!input) {
      input = document.createElement('input');
      input.id = 'player-name-input';
      input.type = 'text';
      input.placeholder = 'Enter your name...';
      input.maxLength = 20;
      document.getElementById('game-container').appendChild(input);
    }
    
    // Position relative to the game container (which wraps the canvas)
    // The canvas is 800x600, so x/y are game coordinates
    const inputWidth = 200;
    const inputHeight = 44;
    
    input.style.cssText = `
      position: absolute;
      width: ${inputWidth}px;
      padding: 12px 16px;
      font-size: 16px;
      font-family: 'Courier New', monospace;
      background: #2a2a4e;
      border: 2px solid #f4d03f;
      border-radius: 8px;
      color: #fff;
      text-align: center;
      outline: none;
      z-index: 1000;
      left: ${x - inputWidth/2}px;
      top: ${y - inputHeight/2}px;
    `;
    
    input.style.display = 'block';
    
    // Pre-fill with saved name if available
    const savedName = localStorage.getItem('projectCoffee_playerName');
    input.value = savedName || '';
    input.focus();
  }
  
  createButton(x, y, text, callback, secondary = false) {
    const bg = this.add.rectangle(x, y, 200, 45, secondary ? 0x333333 : 0xf4d03f);
    const label = this.add.text(x, y, text, {
      fontSize: '18px',
      fill: secondary ? '#aaa' : '#1a1a2e',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    
    bg.setInteractive({ useHandCursor: true });
    
    bg.on('pointerover', () => {
      bg.setFillStyle(secondary ? 0x444444 : 0xffffff);
    });
    
    bg.on('pointerout', () => {
      bg.setFillStyle(secondary ? 0x333333 : 0xf4d03f);
    });
    
    bg.on('pointerdown', callback);
    
    return { bg, label };
  }
  
  createStartButton(x, y) {
    let btn = document.getElementById('start-shift-btn');
    
    if (!btn) {
      btn = document.createElement('button');
      btn.id = 'start-shift-btn';
      btn.textContent = 'START SHIFT ☕';
      document.getElementById('game-container').appendChild(btn);
    }
    
    const btnWidth = 200;
    const btnHeight = 45;
    
    btn.style.cssText = `
      position: absolute;
      width: ${btnWidth}px;
      height: ${btnHeight}px;
      left: ${x - btnWidth/2}px;
      top: ${y - btnHeight/2}px;
      font-size: 18px;
      font-family: 'Courier New', monospace;
      font-weight: bold;
      background: #f4d03f;
      border: none;
      border-radius: 8px;
      color: #1a1a2e;
      cursor: pointer;
      z-index: 1000;
      transition: background 0.2s;
    `;
    
    btn.onmouseover = () => btn.style.background = '#fff';
    btn.onmouseout = () => btn.style.background = '#f4d03f';
    btn.style.display = 'block';
    
    btn.onclick = async () => {
      const nameInput = document.getElementById('player-name-input');
      const playerName = nameInput?.value.trim() || 'friend';
      
      // Store the name globally AND in localStorage
      window.playerName = playerName;
      localStorage.setItem('projectCoffee_playerName', playerName);
      if (window.dialogueManager) {
        window.dialogueManager.playerName = playerName;
      }
      
      // Hide the inputs
      if (nameInput) nameInput.style.display = 'none';
      btn.style.display = 'none';
      
      console.log('Starting game with player name:', playerName);
      
      try {
        await authManager.signInAnonymously();
        console.log('Auth successful, starting game...');
        this.startGame();
      } catch (error) {
        console.error('Auth error (continuing anyway):', error);
        this.startGame();
      }
    };
  }
  
  startGame() {
    console.log('TitleScene.startGame() called - transitioning to CoffeeShopScene');
    // Hide any lingering DOM elements
    const nameInput = document.getElementById('player-name-input');
    const startBtn = document.getElementById('start-shift-btn');
    if (nameInput) nameInput.style.display = 'none';
    if (startBtn) startBtn.style.display = 'none';
    
    console.log('Stopping TitleScene and starting CoffeeShopScene');
    this.scene.stop('TitleScene');
    this.scene.start('CoffeeShopScene');
    console.log('Scene transition initiated');
  }
}
